import { defaultLocale, type Locale } from "@/lib/i18n/config";
import { getAvailableLocalesForItem } from "@/lib/data";

/**
 * Which locale URLs a per-record page really exists in — the hreflang cluster passed to
 * useHeadTags.
 *
 * This answers "does the page exist under that locale", NOT "do we advertise it to Google" —
 * locale-availability.ts#linkLocaleFor reads it to decide whether an internal link keeps the
 * reader's locale or falls back to English. Do not filter INDEXABLE_LOCALES here: that would send
 * every link on a /de, /id or /th page to its English target and break navigation for those
 * readers. The index-eligibility filter belongs at the single choke point that emits head tags —
 * useHeadTags for the client, buildHead for the prerender.
 *
 * These mirror scripts/generate-sitemap.ts#availableLocalesFor and
 * generate-static-meta.ts#readContentMetas. That duplication is deliberate and unavoidable: the
 * scripts run in Node against src/data + the content directories, the app runs in the browser
 * against the same data through Vite. What must not diverge is the *rule*, so keep the two sides
 * in step — a page whose prerendered head lists three locales and whose hydrated head lists five
 * asks Google to index URLs the sitemap deliberately withheld.
 *
 * Reads the small generated itemId -> locale-codes index rather than lib/data.ts's
 * `getItemTranslation`, and that's deliberate, not a style choice: the actual translated *names*
 * load one locale at a time now (see lib/data.ts's doc comment), so at hydration time only the
 * current locale's names are in memory — deriving the hreflang set from that would make it
 * correct for the current locale and wrong (too few alternates) for every other one, on every
 * page, every time. This index carries no name strings, only which locales exist, so it's cheap
 * enough to load unconditionally and stays correct before any locale chunk has loaded.
 */

/**
 * English always; another locale only when the catalog actually has a translated name for that
 * item. Without a name the page would render the English item under a non-English locale — a soft
 * duplicate, which is why the prerender writes no file for it either.
 */
export function localesForItem(itemId: string): Locale[] {
  return [defaultLocale, ...getAvailableLocalesForItem(itemId)];
}
